import { isNaN } from 'lodash-es';
import { isNullDataPoint } from '@customComp/Chart/chartConfigs/nullHandling/isNullDataPoint';
import type {
  IAxisPercentPointCore,
  IAxisPercentSeriesItem,
  TAxisChartSeriesInput,
} from './types';

function getCellRawValue(
  data: TAxisChartSeriesInput['data'],
  xIndex: number,
): string | number | null | undefined {
  const cell = data?.[xIndex];
  if (cell !== null && cell !== undefined && typeof cell === 'object') {
    return cell.value;
  }
  return cell as string | number | null | undefined;
}

function rawToNumeric(
  raw: string | number | null | undefined,
): number {
  if (isNullDataPoint(raw)) {
    return 0;
  }
  const num = Number(raw);
  return isNaN(num) ? 0 : num;
}

/**
 * 占比轴图表（PercentStackBar / PercentLine）的共享数学层。
 *
 * 按 x 轴类目汇总各系列原始 metric 值，计算每点 oPercent 及 Tooltip 元数据。
 * 空原始值按 0 参与分母与分子；同 x 点有效系列 oPercent 合计 100%（total=0 时均为 0）。
 * 本函数不处理 null 的几何/UI 表现，value 留 null 由后续 line builder 的 nullHandling 层填充。
 */
export function computeAxisPercentPoints(
  seriesData: TAxisChartSeriesInput[] | undefined,
  xAxisData: string[] | undefined,
): IAxisPercentSeriesItem[] {
  if (!seriesData?.length) {
    return [];
  }

  const percentSeries: IAxisPercentSeriesItem[] = [];

  // 外层按 x 轴类目遍历：每个类目独立算一次「各系列占该类目总和的百分比」
  xAxisData?.forEach((_xItem, xIndex) => {
    // 分母 = 当前 x 类目下所有系列的原始 metric 之和（空值视为 0，仍计入 total）
    let total = 0;
    for (const series of seriesData) {
      const raw = getCellRawValue(series?.data, xIndex);
      total += rawToNumeric(raw);
    }

    // 内层按系列生成占比点：结构与输入 series 一一对应，供 bar/line builder 复用
    seriesData.forEach((series, sIndex) => {
      if (!percentSeries[sIndex]) {
        // 保留系列级 ECharts 配置（type、stack、lineStyle 等），仅替换 data 为占比点数组
        percentSeries[sIndex] = {
          ...series,
          data: [],
        };
      }

      const { data, metric, dimensions, valueType } = series;
      const raw = getCellRawValue(data, xIndex);
      const numeric = rawToNumeric(raw);
      // total=0 时避免除零，各系列占比统一为 0（如无有效数据的类目）
      const percent = total === 0 ? 0 : (numeric / total) * 100;

      const point: IAxisPercentPointCore = {
        // value 不在此层赋值：PercentLine 需由 nullHandling 决定是否绘制及 Y 轴取值
        value: null,
        // oPercent 为数值型占比，供 Tooltip 排序与 PercentLine 几何计算
        oPercent: percent,
        metricValue: raw,
        percent: `${percent.toFixed(2)}%`,
        metricName: String(metric ?? ''),
        // 标记原始指标是否为百分比类型，Tooltip 格式化时区分展示规则
        isPercent: valueType === 'percent',
        dimensions,
        dateText: '',
      };

      percentSeries[sIndex].data.push(point);
    });
  });

  return percentSeries;
}
